<template>
    <div>
        <v-server-table :name="tableName" :ref="tableName" url="/corrective-actions/list" :columns="columns" :options="options">

            <div slot="afterFilterWrapper" class="ml-md-2 ml-0 mt-md-0 mt-2 mb-md-0 mb-3 float-md-left">
                <div class="clearfix d-md-none d-block"></div>

                <div v-if="allowFilterSaving" class="float-left mr-1">
                    <user-filters ref="caSavedFilters" assetType="CorrectiveAction" :filterKey="filterKey" @saving="setUserFilterData" @show="setUserFilterData"></user-filters>
                </div>
                <div class="float-left">
                    <table-filters-2  ref="caTableFilters"
                        filterUrl="/corrective-actions/filters"
                        v-bind:filters="filters"
                        :emptyFilters="filterDefs"
                        :filterKey="filterKey"
                        @filtersChanged="refreshTableData"
                        @filtersCleared="filtersCleared"
                        v-show="!dashboardFilters">
                        <template slot-scope="slotProps" v-bind:canViewAllCorrectiveActions="canViewAllCorrectiveActions" v-bind:canViewJobsites="this.canViewJobsites">
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['corrective_actions.status_id']" :btnLabel="(values) => ('Statuses ('+values.length+')')" :selectOptions="slotProps.filterData.statuses" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['jobsite.DivisionId']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="slotProps.filterData.divisions" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector buttonTitle="Created Date" v-model="slotProps.filters['corrective_actions.created_at']" />
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector buttonTitle="Due Date" v-model="slotProps.filters['corrective_actions.due_date']" />
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector buttonTitle="Completed Date" v-model="slotProps.filters['corrective_actions.completed_at']" />
                            </div>

                            <div class="mt-2" v-if="canViewJobsites || canViewAllCorrectiveActions">
                               <hr/>
                               <div class="d-inline-block" style="width: 300px;" v-if="canViewJobsites">
                                   <jobsite-select :selectedValues="slotProps.filters['corrective_actions.JobsiteId']" :forFilters="true" v-on:vselected="((data) => { slotProps.filters['corrective_actions.JobsiteId'] = data || {};  })" />
                               </div>
                               <div class="d-inline-block" style="width: 250px;" v-if="canViewAllCorrectiveActions">
                                    <user-select :selectedValues="slotProps.filters['corrective_actions.assignee_id']"
                                        :forFilters="true"
                                        placeholder="Assignees"
                                        v-on:vuselected="((data) => { slotProps.filters['corrective_actions.assignee_id'] = data || {};  })"
                                        :appendCurrentUserOption="true" />
                               </div>
                            </div>

                        </template>
                    </table-filters-2>
                </div>

                <button type="button" class="btn btn-outline-success ml-2" @click="exportDataTable()" :disabled="exportingTable">
                    <template v-if="!exportingTable"><i class="fa fa-file-excel-o"></i> Export</template>
                    <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Exporting</template>
                </button>

                <div class="clearfix d-md-none d-block"></div>
            </div>

            <template slot="corrective_action_id" slot-scope="props">
                <span v-if="assetType == 'Dashboard'" style="white-space: nowrap;">
                    <router-link :to="`/corrective-actions/view/${props.row.corrective_action_id}`" class="btn btn-sm btn-light mr-1 mb-xl-0 mb-lg-2 mb-0 d-xl-inline d-lg-block d-inline">
                        <i class="fa fa-eye"></i> View
                    </router-link>
                    <!-- <router-link :to="`/corrective-actions/edit/${props.row.corrective_action_id}`">Edit</router-link>&nbsp;|&nbsp; -->
                    <router-link :to="`/corrective-actions/complete/${props.row.corrective_action_id}`" class="btn btn-sm btn-light">
                        <i class="fa fa-flag-checkered"></i> Complete
                    </router-link>
                </span>
                <router-link v-else slot="corrective_action_id" slot-scope="props" :to="`/corrective-actions/view/${props.row.corrective_action_id}`" title="View Corrective Action" class="btn btn-sm btn-light">
                    View CA-{{ props.row.corrective_action_id }}
                </router-link>
            </template>

            <template slot="asset_type" slot-scope="props">
                <router-link :to="`/${assetTypeUrl(props.row.asset_type)}/${props.row.asset_id}`" v-if="props.row.asset_type === 'InspectionResult' && $root.hasPermission(['inspections.view.browse', 'inspections.view.own'])">
                    {{ props.row.asset_type }} {{ props.row.asset_id }}
                </router-link>
                <router-link :to="`/${assetTypeUrl(props.row.asset_type)}/${props.row.asset_id}`" v-else-if="props.row.asset_type === 'Observation' && $root.hasPermission(['inspections.view.browse', 'inspections.view.own'])">
                    {{ props.row.asset_type }} {{ props.row.asset_id }}
                </router-link>
                <router-link :to="`/${assetTypeUrl(props.row.asset_type)}/${props.row.asset_id}`" v-else-if="props.row.asset_type === 'Incident' && $root.hasPermission(['incidents.view.browse', 'incidents.view.own'])">
                    {{ props.row.asset_type }} {{ props.row.asset_id }}
                </router-link>
                <span v-else>
                    {{ props.row.asset_type }} {{ props.row.asset_id }}
                </span>
            </template>

            <template slot="JobsiteName" slot-scope="props">
                <div v-if="props.row.JobsiteId">
                    <router-link :to="`/jobsites/view/${props.row.JobsiteId}`" :title="`View Jobsite`" v-if="canViewJobsites">
                        {{ props.row.JobsiteNumber }} - {{ props.row.JobsiteName }}
                    </router-link>
                    <span v-else>
                        {{ props.row.JobsiteNumber }} - {{ props.row.JobsiteName }}
                    </span>
                </div>
                <div v-else>
                    {{ props.row.JobsiteName }}
                </div>
            </template>

            <template slot="DivisionName" slot-scope="props">
                <span v-if="props.row.division_code">{{ props.row.division_code }} - </span>{{ props.row.DivisionName }}
            </template>

            <template slot="status_name" slot-scope="props">
                <label :class="`badge badge-${props.row.status_color}`">
                    {{ props.row.status_name }}
                </label>
            </template>

            <template slot="assignee_first_name" slot-scope="props">
                <label class="badge badge-warning small" v-if="props.row.employee_subcontractor_id" title="Subcontractor User">SUB</label>
                <user-name :user="assignee(props.row)" />
            </template>

            <template slot="creator_first_name" slot-scope="props">
                <user-name :user="creator(props.row)" />
            </template>

            <template slot="completer_first_name" slot-scope="props">
                <user-name :user="completer(props.row)" />
            </template>

            <template slot="other_assignees" slot-scope="props">
                <template v-if="props.row.other_assignees">
                    <span v-for="(assign, i) in props.row.other_assignees">
                        <template v-if="i > 0">, </template>{{ assign.FirstName }} {{ assign.LastName }}
                    </span>
                </template>
            </template>

            <template slot="subcontractor" slot-scope="props">
                <div v-if="props.row.subcontractor_id">
                    <router-link :to="`/subcontractors/view/${props.row.subcontractor_id}`" :title="`View Subcontractor`" v-if="$root.hasPermission('subcontractors.view')">
                        {{ props.row.subcontractor_name }}
                    </router-link>
                    <span v-else-if="props.row.subcontractor">
                        {{ props.row.subcontractor }}
                    </span>
                </div>
            </template>

            <template slot="completed_at" slot-scope="props">
                <template v-if="props.row.completed_at">{{ props.row.completed_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</template>
            </template>

            <template slot="created_at" slot-scope="props">
                <template v-if="props.row.created_at">{{ props.row.created_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</template>
            </template>

            <template slot="due_date" slot-scope="props">
                <label v-if="props.row.due_date && props.row.status_context != 'Closed' && isOverdueVar(props.row.due_date)" class="badge badge-danger" title="Action is Overdue">
                    {{ props.row.due_date | moment("MM/DD/YYYY") }}
                </label>
                <template v-else-if="props.row.due_date">
                    {{ props.row.due_date | moment("MM/DD/YYYY") }}
                </template>
            </template>

            <template slot="child_row" slot-scope="props">
                <span class="font-weight-bold">Details:</span><br>
                <div v-html="props.row.detail"></div>
            </template>

        </v-server-table>
    </div>
</template>

<script>
    import moment from 'moment-timezone';
    import filterExtractor from '@/helpers/TableFilterExtractor';
    import customVTColDropdown from '@/components/vue-tables-2/CustomColumnDropdown';

    export default {
        //props: ['assetType', 'assetId', 'allowFilterSaving'],
        props: {
            assetType: {
                type: String,
                default: null,
            },

            assetId: {
                //type: Number,
                default: null,
            },

            allowFilterSaving: {
                //type: Boolean,
                default: false,
            },

            filterId: {
                type: Number,
                default: null,
            },

            dashboardFilters: {
                type: Object,
                default(){
                    return null;
                },
            },

            tableConfig: {
                type: Object,
                default(){
                    return null;
                },
            },
        },

        data: function() {
            return {
                exportingTable: false,
                userTimezone: this.$store.state.user.timezone,
                today: moment().format('YYYY-MM-DD'),
                isOverdueVar: this.isOverdue,
                tableName: 'correctiveActionsTable',

                filterKey: 'correctiveActionFilters',

                filterDefs: {
                    'corrective_actions.created_at': '',
                    'corrective_actions.due_date': '',
                    'corrective_actions.completed_at': '',
                    'jobsite.DivisionId': [],
                    'corrective_actions.status_id': [],
                    'corrective_actions.assignee_id': {},
                    'corrective_actions.JobsiteId': {},
                },
            }
        },

        watch: {
            appliedFilter(newVal, oldVal){
                //Call the helper
                this.checkForAppliedFilter();
            },
        },

        computed: {
            canViewAllCorrectiveActions(){
                return this.$root.hasPermission('correctiveActions.view.browse');
            },

            canViewJobsites(){
                return this.$root.hasPermission('jobsites.view');
            },

            columns() {
                if (this.assetType == 'Dashboard')
                {
                    return [
                        'corrective_action_id',
                        'asset_type',
                        'status_name',
                        'creator_first_name',
                        'assignee_first_name',
                        'other_assignees',
                        'due_date',
                    ];
                }
                return [
                    'corrective_action_id',
                    'asset_type',
                    'JobsiteName',
                    'DivisionName',
                    'status_name',
                    'creator_first_name',
                    'assignee_first_name',
                    'completer_first_name',
                    'other_assignees',
                    'subcontractor',
                    'created_at',
                    'due_date',
                    'completed_at',
                    'hours_to_completion'
                ];
            },

            options(){
                return {
                    headings: {
                        corrective_action_id: 'View',
                        asset_type: 'Attached To',
                        JobsiteName: 'Jobsite',
                        DivisionName: 'Division',
                        status_name: 'Status',
                        assignee_first_name: this.assetType != 'Dashboard' ? 'Assignee' : 'Assigned To',
                        completer_first_name: 'Completer',
                        due_date: 'Due',
                        completed_at: 'Completed',
                        creator_first_name: this.assetType != 'Dashboard' ? 'Creator' : 'Created By',
                        other_assignees: 'Other Assignees',
                        created_at: 'Created',
                        hours_to_completion: 'Hours to Completion',
                    },
                    saveState: this.allowFilterSaving,
                    columnsDropdown: customVTColDropdown,
                    visibleColumns: (this.allowFilterSaving ? this.$root.getVisibleColumnsFromState(this.tableName) : false),
                    sendInitialRequest: (this.filterId ? false : true),
                    orderBy: {
                        ascending: false,
                        column: 'corrective_actions.created_at',
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        this.$emit('correctiveActionCount', response.data.count);

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Set the filters
                        data.filters = (this.dashboardFilters ? filterExtractor.extractFilters(this.dashboardFilters) : filterExtractor.extractFilters(this.filters));

                        //INclude the asset
                        data.assetType = this.assetType;
                        data.assetId = this.assetId;

                        //Send it
                        return data;
                    },
                    uniqueKey: 'corrective_action_id',
                    showChildRowToggler: this.assetType != 'Dashboard',
                };
            },

            filters(){
                //Check State
                if(this.$store.state.tableFilters.filters[this.filterKey])
                {
                    //Send it
                    return this.$store.state.tableFilters.filters[this.filterKey];
                }

                //Return Definition
                return JSON.parse(JSON.stringify(this.filterDefs));
            },

            appliedFilter(){
                //Send it
                return this.$store.state.appliedFilters[this.filterKey] ? this.$store.state.appliedFilters[this.filterKey] : null;
            },
        },

        methods: {
            assignee(row) {
                return {
                    FirstName: row.assignee_first_name,
                    LastName: row.assignee_last_name,
                    deleted_at: row.assignee_deleted_at,
                };
            },

            creator(row) {
                return {
                    FirstName: row.creator_first_name,
                    LastName: row.creator_last_name,
                    deleted_at: row.creator_deleted_at,
                };
            },

            completer(row) {
                return {
                    FirstName: row.completer_first_name,
                    LastName: row.completer_last_name,
                    deleted_at: row.completer_deleted_at,
                };
            },

            assetTypeUrl: function(asset_type) {
                if (asset_type == 'InspectionResult')
                    return 'inspections/result';
                return asset_type+'s/view'
            },

            exportDataTable(){
                //Loading indicator
                this.exportingTable = true;

                //Get the Query Stuff
                var params = this.$refs.correctiveActionsTable.getRequestParams();

                //Build the payload
                var payload = {
                    filters: filterExtractor.extractFilters(this.filters),
                    query: params.query,
                    orderBy: this.$refs.correctiveActionsTable.options.orderBy.column,
                    ascending: this.$refs.correctiveActionsTable.options.orderBy.ascending,
                    table: 'correctiveActionsTable',
                    columns: this.$refs.correctiveActionsTable.columns,
                    visible_columns: this.$root.getVisibleColumnsFromState(this.tableName),
                    export: true,
                    assetType: this.assetType,
                    assetId: this.assetId,
                };

                //Send it
                this.$http.get('/corrective-actions/list', payload)
                    .then(response => {
                        //Open the new window
                        window.open(response.data);
                    })
                    .catch(err => {
                        //Alert
                        this.$toastr.error(err.response.data, 'Failed to Export Corrective Actions');
                    })
                    .then(response => {
                        //reset
                        this.exportingTable = false;
                    });
            },

            isOverdue(date){
                //Testing
                //console.log(date);
                //return true;

                //Check if date is GREATER than today
                var d = moment(date);

                //Send it
               return d.isBefore(this.today);
            },

            refreshTableData(filters) {
                //Set the data
                this.$refs.correctiveActionsTable.setPage(1);
                //this.$refs.correctiveActionsTable.getData();
            },

            filtersCleared(){
                //Reset
                this.refreshTableData();

                //Reset the Columns
                this.$refs.caTableFilters.resetColVis(this.$refs.correctiveActionsTable);
            },

            setUserFilterData(){
                //Call the Helper
                this.$refs.caSavedFilters.setFilterParams(this.$refs.correctiveActionsTable);
            },

            checkForAppliedFilter(){
                //Check if
                if(!this.allowFilterSaving || !this.appliedFilter)
                {
                    return;
                }

                //Call the helper
                this.$refs.caTableFilters.setColVis(this.$refs.correctiveActionsTable);
            },
        },

        mounted(){
            //Check for a saved filter
            this.checkForAppliedFilter();

            //Check for Filter ID
            if(this.filterId)
            {
                this.$refs.caSavedFilters.setSavedFilter(this.filterId);
            }

            //Check for Table Config
            if(this.tableConfig)
            {
                //Set it
                this.$refs.caTableFilters.setColVisFromSavedFilter(this.$refs.correctiveActionsTable, this.tableConfig);
            }
        }
    }
</script>
